/*!
 * swiper-preload.js — promote upcoming slides on Swiper change.
 *
 * Background:
 *   Mobile PDP gallery + recommended-products + featured-product all
 *   use Swiper carousels. We eager-load the first 2-3 slides at HTML
 *   render time (commits 61a58c5 / a323ab2 / e77a6d1), but slides
 *   4+ stay loading="lazy" to preserve bandwidth on long galleries
 *   (e.g. 12-image PDPs).
 *
 *   When the user swipes past slide 3, the next image only STARTS
 *   fetching at swipe time — visible 200-2000 ms blank, especially
 *   on slow networks.
 *
 * Fix:
 *   Listen for Swiper's `slideChange` event. On every change, walk
 *   forward N slides from the new active index and promote any
 *   `loading="lazy"` images to `loading="eager"` — which forces the
 *   browser to start the fetch immediately.
 *
 *   Combined with the eager-by-default first-3 slides, this means:
 *     - First 3 slides:           ready before any swipe (HTML-time)
 *     - On swipe to slide 1:      slides 1-4 promoted (slide 4 was lazy)
 *     - On swipe to slide 2:      slides 2-5 promoted
 *     - ... etc
 *
 *   So the user is always 3 slides ahead of where they currently are.
 *
 * Why this is safe:
 *   - Only promotes loading="lazy" → loading="eager" (no other state changes).
 *   - Never DEMOTES eager → lazy (which would be silly).
 *   - Each img is only promoted once (after promotion the selector
 *     won't match again — `loading="eager"` is sticky).
 *   - Idempotent per Swiper instance via __preloadHookAttached flag.
 *   - MutationObserver catches Swiper instances initialized after this
 *     script loads (e.g. Stiletto's section re-renders, dynamically
 *     fetched product carousels).
 */

(function () {
  if (window.__swiperPreloadInit) return;
  window.__swiperPreloadInit = true;

  var PRELOAD_AHEAD = 3;

  // Selector for Swiper containers we want to enhance. Focused on
  // carousels where blank-on-swipe is a real UX problem.
  // - .product__media-container.swiper covers BOTH the main PDP gallery
  //   (in main-product--default.liquid) AND the featured-product section's
  //   carousel — they share the same wrapper class.
  // - .recommended-products__content.swiper is the "More For You" carousel.
  // Header marquees, banner sliders etc are intentionally excluded.
  var TARGET_SELECTOR = [
    '.product__media-container.swiper',
    '.recommended-products__content.swiper'
  ].join(', ');

  function attachHook(swiperEl) {
    var swiper = swiperEl.swiper;
    if (!swiper) return;
    if (swiper.__preloadHookAttached) return;
    swiper.__preloadHookAttached = true;

    function promote() {
      var idx = swiper.activeIndex || 0;
      var slides = swiper.slides;
      if (!slides || !slides.length) return;
      var max = Math.min(idx + PRELOAD_AHEAD, slides.length - 1);
      for (var i = idx; i <= max; i++) {
        var slide = slides[i];
        if (!slide || !slide.querySelectorAll) continue;
        var lazyImgs = slide.querySelectorAll('img[loading="lazy"]');
        for (var j = 0; j < lazyImgs.length; j++) {
          // Setting `loading = 'eager'` on an off-screen lazy image
          // triggers an immediate fetch. Documented Chrome behaviour;
          // also works in Firefox & Safari.
          lazyImgs[j].loading = 'eager';
        }
      }
    }

    swiper.on('slideChange', promote);
    // Also run once at attach time — covers slide 0's neighbours in case
    // our script ran AFTER Swiper finished initializing.
    promote();
  }

  function attachToAll() {
    var els = document.querySelectorAll(TARGET_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      // .swiper-initialized is added by Swiper itself when setup is done.
      if (els[i].classList.contains('swiper-initialized')) {
        attachHook(els[i]);
      }
    }
  }

  // Initial pass — handles Swipers that are ready when this script runs.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachToAll);
  } else {
    attachToAll();
  }

  // Watch for Swipers that initialize later. Stiletto's section re-renders
  // (variant change, recommendation fetch, etc.) detach + re-init Swiper.
  if (typeof MutationObserver !== 'undefined') {
    var debounceTimer = null;
    function debouncedAttach() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(attachToAll, 50);
    }
    var observer = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        // Class changes on existing nodes (e.g. .swiper-initialized added).
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (m.target.classList && m.target.classList.contains('swiper-initialized')) {
            debouncedAttach();
            return;
          }
        }
        // Newly added nodes (e.g. dynamically rendered carousels).
        if (m.type === 'childList' && m.addedNodes.length) {
          debouncedAttach();
          return;
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      childList: true
    });
  }
})();
